import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, costFormater, PriceInput, SaveCancelControls, Text, useResponsive } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { EditableBadge } from '@/modules/UseMyMoney/components';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { useAmountOptions } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';

import FieldSetupLabel from '../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../FieldSetupOptions/FieldSetupOptions';

const AmountFieldSetup = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values, handleSetFormValues } = useMyMoneyFormStore();
  const [amount, setAmount] = useState(values?.customAmount || '');
  const { suggestedAmountOptions, selectedAmountFromSuggested } = useAmountOptions({ amount });
  const [isEditMode, setIsEditMode] = useState(!!selectedAmountFromSuggested);

  const handleChangeSuggestedAmount = useCallback((amount: string) => {
    handleSetFormValues({ amount });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  const handleChangeAmount = useCallback((amount: string) => {
    handleSetFormValues({ amount, customAmount: amount });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  const handleSave = useCallback(() => {
    const newAmount = toNumber(amount) ? amount : '';
    handleSetFormValues({ amount: newAmount, customAmount: newAmount });
    setIsEditMode(false);
  }, [handleSetFormValues, amount]);

  const handleCancel = useCallback(() => {
    handleSetFormValues({ amount: '', customAmount: '' });
    setAmount('');
  }, [handleSetFormValues]);

  return (
    <Box data-testid="amount-setup">
      <FieldSetupLabel
        testId="amount"
        hasValue={!!values?.amount}
        title={t('How much was this expense?')}
        isMobile={isMobile}
      />

      {suggestedAmountOptions?.length ? (
        <Box margin={{ top: 'spacing24' }}>
          <Text weight={700}>
            {suggestedAmountOptions?.length > 1 
              ? t('Amounts found on your document')
              : t('Amount found on your document')}
          </Text>
          <FieldSetupOptions
            testId="suggested-amount"
            margin={{ top: 'spacing8' }}
            value={values?.amount}
            onChangeValue={handleChangeSuggestedAmount}
            options={suggestedAmountOptions}
          />
        </Box>
      ) : null}

      {/* Custom amount setup */}
      <Box margin={{ top: 'spacing24' }}>
        <Text weight={700}>
          {t('Not right? Enter the amount paid.')}
        </Text>
        <Box margin={{ top: 'spacing8' }} align="start">
          <EditableBadge
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            isSelected={Number(values?.amount) === Number(values?.customAmount)}
            value={values?.customAmount || ''}
            title={values?.customAmount ? `${costFormater(toNumber(values?.customAmount), true)}` : undefined}
            onChangeValue={handleChangeAmount}
          >
            <Box width={isMobile ? '100%' : '160px'}>
              <SaveCancelControls
                testId="amount"
                onSave={handleSave}
                onCancel={handleCancel}
              >
                <PriceInput
                  name="amount"
                  testId="amount"
                  value={amount}
                  onChange={setAmount}
                  placeholder={t('Amount')}
                  disableBrowserHints
                />
              </SaveCancelControls>
            </Box>
          </EditableBadge>
        </Box>
      </Box>
    </Box>
  );
};

export default AmountFieldSetup;
